import {StateCreator} from "zustand/vanilla"
import {
  ArchiveMemoryResponse,
  ApproveDecisionMemoryResponse,
  CreateMemoryRequest,
  CreateMemoryResponse,
  ListMemoriesRequest,
  ListMemoriesResponse,
  Memory,
  MemoryListView,
  RejectDecisionMemoryRequest,
  RejectDecisionMemoryResponse,
  RestoreMemoryResponse,
  UpdateMemoryRequest,
  UpdateMemoryResponse,
} from "@devplaninc/codeup"
import {
  workspaceMemoriesListAPI,
  workspaceMemoriesAPI,
  workspaceMemoryAPI,
  workspaceMemoryApproveAPI,
  workspaceMemoryArchiveAPI,
  workspaceMemoryRejectAPI,
  workspaceMemoryRestoreAPI,
} from "@/app/apiPaths"
import {jr} from "@/hooks/queries/response-handler"

export type MemoryInput = Pick<Memory, "title" | "body" | "whenToUse" | "expirationDate" | "labels">
export type CreateMemoryInput = MemoryInput & Pick<Memory, "kind">

export type WorkspaceMemoriesStoreState = {
  workspaceMemories: Record<number, Record<string, Memory>>
  fetchWorkspaceMemories(companyId: number, archived: boolean, pageToken?: string): Promise<ListMemoriesResponse>
  fetchDecisionMemories(companyId: number, rejected: boolean, pageToken?: string): Promise<ListMemoriesResponse>
  createWorkspaceMemory(companyId: number, input: CreateMemoryInput): Promise<Memory>
  updateWorkspaceMemory(companyId: number, input: {memoryId: string; values: MemoryInput}): Promise<Memory>
  archiveWorkspaceMemory(companyId: number, memory: Memory): Promise<Memory>
  restoreWorkspaceMemory(companyId: number, memory: Memory): Promise<Memory>
  approveDecisionMemory(companyId: number, memory: Memory): Promise<Memory>
  rejectDecisionMemory(companyId: number, input: {memory: Memory; reason: string}): Promise<Memory>
}

export const createWorkspaceMemoriesSlice: StateCreator<
  WorkspaceMemoriesStoreState,
  [],
  [],
  WorkspaceMemoriesStoreState
> = (set) => ({
  workspaceMemories: {},
  fetchWorkspaceMemories: async (companyId, archived, pageToken = "") => {
    const response = await fetch(workspaceMemoriesListAPI(companyId), {
      method: "POST",
      body: JSON.stringify(ListMemoriesRequest.toJSON(ListMemoriesRequest.create({
        workspaceId: String(companyId),
        archived,
        view: archived ? MemoryListView.MEMORY_LIST_VIEW_ARCHIVED : MemoryListView.MEMORY_LIST_VIEW_ACTIVE,
        pageToken,
      }))),
    })
    const js = await jr(response)
    const result = ListMemoriesResponse.fromJSON(js)
    set(s => ({
      ...s,
      workspaceMemories: {
        ...s.workspaceMemories,
        [companyId]: upsertMemories(s.workspaceMemories[companyId], result.memories),
      },
    }))
    return result
  },
  fetchDecisionMemories: async (companyId, rejected, pageToken = "") => {
    const response = await fetch(workspaceMemoriesListAPI(companyId), {
      method: "POST",
      body: JSON.stringify(ListMemoriesRequest.toJSON(ListMemoriesRequest.create({
        workspaceId: String(companyId),
        view: rejected
          ? MemoryListView.MEMORY_LIST_VIEW_REJECTED_DECISIONS
          : MemoryListView.MEMORY_LIST_VIEW_PENDING_DECISIONS,
        pageToken,
      }))),
    })
    const js = await jr(response)
    const result = ListMemoriesResponse.fromJSON(js)
    set(s => ({
      ...s,
      workspaceMemories: {
        ...s.workspaceMemories,
        [companyId]: upsertMemories(s.workspaceMemories[companyId], result.memories),
      },
    }))
    return result
  },
  createWorkspaceMemory: async (companyId, input) => {
    const response = await fetch(workspaceMemoriesAPI(companyId), {
      method: "POST",
      body: JSON.stringify(CreateMemoryRequest.toJSON(CreateMemoryRequest.create({
        memory: Memory.create({
          workspaceId: String(companyId),
          ...input,
        }),
      }))),
    })
    const js = await jr(response)
    const {memory} = CreateMemoryResponse.fromJSON(js)
    if (!memory) throw new Error("Memory creation response did not include a memory")
    setMemory(set, companyId, memory)
    return memory
  },
  updateWorkspaceMemory: async (companyId, input) => {
    const response = await fetch(workspaceMemoryAPI(companyId, input.memoryId), {
      method: "PATCH",
      body: JSON.stringify(UpdateMemoryRequest.toJSON(UpdateMemoryRequest.create({
        memory: Memory.create({
          id: input.memoryId,
          workspaceId: String(companyId),
          ...input.values,
        }),
      }))),
    })
    const js = await jr(response)
    const {memory} = UpdateMemoryResponse.fromJSON(js)
    if (!memory) throw new Error("Memory update response did not include a memory")
    setMemory(set, companyId, memory)
    return memory
  },
  archiveWorkspaceMemory: async (companyId, memory) => {
    const response = await fetch(workspaceMemoryArchiveAPI(companyId, memory.id), {method: "POST"})
    const js = await jr(response)
    const {memory: archivedMemory} = ArchiveMemoryResponse.fromJSON(js)
    if (!archivedMemory) throw new Error("Memory archive response did not include a memory")
    setMemory(set, companyId, archivedMemory)
    return archivedMemory
  },
  restoreWorkspaceMemory: async (companyId, memory) => {
    const response = await fetch(workspaceMemoryRestoreAPI(companyId, memory.id), {method: "POST"})
    const js = await jr(response)
    const {memory: restoredMemory} = RestoreMemoryResponse.fromJSON(js)
    if (!restoredMemory) throw new Error("Memory restore response did not include a memory")
    setMemory(set, companyId, restoredMemory)
    return restoredMemory
  },
  approveDecisionMemory: async (companyId, memory) => {
    const response = await fetch(workspaceMemoryApproveAPI(companyId, memory.id), {method: "POST"})
    const js = await jr(response)
    const {memory: approvedMemory} = ApproveDecisionMemoryResponse.fromJSON(js)
    if (!approvedMemory) throw new Error("Decision approval response did not include a memory")
    setMemory(set, companyId, approvedMemory)
    return approvedMemory
  },
  rejectDecisionMemory: async (companyId, input) => {
    const response = await fetch(workspaceMemoryRejectAPI(companyId, input.memory.id), {
      method: "POST",
      body: JSON.stringify(RejectDecisionMemoryRequest.toJSON(RejectDecisionMemoryRequest.create({
        workspaceId: String(companyId),
        memoryId: input.memory.id,
        reason: input.reason,
      }))),
    })
    const js = await jr(response)
    const {memory: rejectedMemory} = RejectDecisionMemoryResponse.fromJSON(js)
    if (!rejectedMemory) throw new Error("Decision rejection response did not include a memory")
    setMemory(set, companyId, rejectedMemory)
    return rejectedMemory
  },
})

function upsertMemories(existing: Record<string, Memory> | undefined, memories: Memory[]): Record<string, Memory> {
  const next = {...(existing || {})}
  for (const memory of memories) {
    next[memory.id] = memory
  }
  return next
}

function setMemory(
  set: Parameters<StateCreator<WorkspaceMemoriesStoreState>>[0],
  companyId: number,
  memory: Memory,
) {
  set(s => ({
    ...s,
    workspaceMemories: {
      ...s.workspaceMemories,
      [companyId]: {
        ...(s.workspaceMemories[companyId] || {}),
        [memory.id]: memory,
      },
    },
  }))
}
